/**
 * Wallet API — Mock Implementation
 *
 * Mock wallet/balance API for development.
 */

import type {
  ApiResponse,
  PaginatedResponse,
} from '../types/common.types'
import type {
  WalletTransaction,
  WalletSummary,
  Payout,
} from '../types/wallet.types'
import { mockDb, delay } from './mockDb'
import { success, paginated } from './client'

export const walletApi = {
  /**
   * Get wallet summary for a user.
   */
  async getWalletSummary(userId: string): Promise<ApiResponse<WalletSummary>> {
    await delay(150)

    const transactions = mockDb
      .getAll<WalletTransaction>('walletTransactions')
      .filter((t) => t.userId === userId)

    const completed = transactions.filter((t) => t.status === 'completed')
    const pending = transactions.filter((t) => t.status === 'pending')

    const totalIn = completed
      .filter((t) => t.type === 'in')
      .reduce((s, t) => s + t.amount, 0)
    const totalOut = completed
      .filter((t) => t.type === 'out')
      .reduce((s, t) => s + t.amount, 0)

    return success({
      balance: totalIn - totalOut,
      totalIn,
      totalOut,
      pendingIn: pending
        .filter((t) => t.type === 'in')
        .reduce((s, t) => s + t.amount, 0),
      pendingOut: pending
        .filter((t) => t.type === 'out')
        .reduce((s, t) => s + t.amount, 0),
      recentTransactions: transactions.slice(-10).reverse(),
    })
  },

  /**
   * Get paginated wallet transactions for a user.
   */
  async getTransactions(
    userId: string,
    page = 1,
    limit = 20
  ): Promise<PaginatedResponse<WalletTransaction>> {
    await delay(100)

    const all = mockDb
      .getAll<WalletTransaction>('walletTransactions')
      .filter((t) => t.userId === userId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))

    return paginated(all, { page, limit })
  },

  /**
   * Get payouts for a vendor.
   */
  async getPayouts(vendorId: string): Promise<ApiResponse<Payout[]>> {
    await delay(100)

    const payouts = mockDb
      .getAll<Payout>('payouts')
      .filter((p) => p.vendorId === vendorId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))

    return success(payouts)
  },
}
